import React, { useEffect, useRef, useState, createRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { FileDropZone, excel } from 'components/layout/mui-dropzone';
import {
  setPostAcqTaskImportChannel, setPostAcqTaskImportRequestType, setImportRows,
  submitImport, setImportName, setConfirmationModalState, setImportRowsSkippedCount,
  setImportRawData, appNotify, setImportNameValidation, setImportDupCheckErrors, setImportLoadingOverlayState, revalidateImportRows
} from 'store/actions';
import { importTypeStateSelector, postAcqTaskImportStageSelector, postAcquisitionTaxonomySelector } from 'store/selectors';
import { getChannels, getRequestTypesForChannel } from 'components/underwrite/postAcquisition/import/importTasks/taxonomyHelpers';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Tooltip,
  Divider,
  Typography,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField
} from '@material-ui/core/';
import { ImportTasksGrid } from 'components/underwrite/postAcquisition/import/importTasks';
import { ImportExpander, InlineBadge } from 'components/underwrite/shared/import';
import xlsxSampleSheet from 'static/assets/ReVUe_SampleImportPostAcq.xlsx';
import { sendClientLog } from 'services/logService';
import debounce from 'lodash/debounce';
import { Constants } from 'utilities/constants';
import { format } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';

const gridRef = createRef();

const _debounce = debounce(fn => fn(), 1000); // Keep this outside component to prevent losing the debounce instance on rerenders

const ImportTasksControls = ({
  classes,
  history,
  channel,
  stage,
  requestType,
  taxonomy,
  canImport,
  importRows,
  importName,
  importNameValidation,
  gridEditDirty,
  dupCheckErrors,
  importRowsSkippedCount,
  setPostAcqTaskImportChannel,
  setPostAcqTaskImportRequestType,
  setImportRows,
  setImportRowsSkippedCount,
  revalidateImportRows,
  setImportDupCheckErrors,
  setConfirmationModalState,
  submitImport,
  setImportName,
  setImportNameValidation,
  redirectToImportStatusTab,
  setImportRawData,
  setImportLoadingOverlayState,
  appNotify
}) => {
  const [filterByErrors, setFilterByErrors] = useState(false);
  const [localImportName, setLocalImportName] = useState(null);
  const dupRowReferenceIds = dupCheckErrors?.map(error => error.referenceId) || [];
  const hasErrors = importRows.some(row => row.invalidColumns?.length > 0) || dupRowReferenceIds.length;
  const maxCountExceeded = importRows?.length > Constants.ImportMaxCount.POSTACQTASKS;

  useEffect(() => {
    sendClientLog('Info', 'Import - User loaded post-acq task import tab');
  }, []);

  // On leaving the import tab, clear channel/request type if no rows are staged so the next visit re-prompts the user.
  // setPostAcqTaskImportChannel('') also resets requestType in the reducer.
  const importRowsRef = useRef(importRows);
  useEffect(() => { importRowsRef.current = importRows; }, [importRows]);
  useEffect(() => () => {
    if (importRowsRef.current.length === 0) {
      setPostAcqTaskImportChannel('');
    }
  }, []);

  useEffect(() => {
    const dataSource = gridRef.current.instance.getDataSource();
    if (!(dataSource && importRows)) return;

    if (filterByErrors) {
      if (!hasErrors) { // If no more errors (or if no importRows), automatically set filter toggle off and remove from grid
        setFilterByErrors(false);
        gridRef.current.instance.clearFilter();
      } else { // Else, apply filter to grid data
        gridRef.current.instance.filter(row => {
          return row.invalidColumns?.length > 0 || dupRowReferenceIds.includes(row.referenceId);
        });
      }
    } else if (gridRef.current.instance.filter()) { // If filter toggle is off, clear filter from grid
      gridRef.current.instance.clearFilter();
    }
  }, [importRows, filterByErrors])

  useEffect(() => {
      if (importName !== localImportName) {
        setLocalImportName(importName);
      }
    }, [importName]);

  const onDropZoneFilesAdded = async (addedFiles) => {
    let combinedData = [];
    try {
      setImportLoadingOverlayState({ // Start displaying loading here since sheets with high row count can take some time. Loading will be turned off in importRawDataSaga after validation is finished.
        isLoading: true,
        message: `Loading file...`
      });
      for (const file of addedFiles) {
        if (excel.excelMimeTypes.indexOf(file.type.toLowerCase()) >= 0) {
          const spreadsheet = await excel.parseExcel(file);
          // Only importing first sheet, should import all?
          console.log(spreadsheet)
          const DragAndDropData = spreadsheet[Object.keys(spreadsheet)[0]];
          combinedData = combinedData.concat(DragAndDropData);
          //console.log(spreadsheet);
          // setImportRawData(spreadsheet);
        } else {
          // const text = await files.loadFileAsText(file);
          //console.log(text);
        }
      }
      if (filterByErrors) {
        setFilterByErrors(false);
      }
      setImportRawData(combinedData, 'file');
    } catch {
      setImportLoadingOverlayState({
        isLoading: false,
        message: null
      });
    }
  }
  const onDropZoneFilesRejected = files => {
    //console.log('Files rejected:', files);
  }

  const handlePaste = async e => {
    // First, ask the Permissions API if we have some kind of access to
    // the "clipboard-read" feature.
    // await navigator.permissions.revoke({name:'clipboard-read'}).then(function(result) {
    //   console.log(result.state);
    // });
    const result = await navigator.permissions.query({ name: 'clipboard-read' });
    console.log(navigator.permissions)
    // If permission to read the clipboard is granted or if the user will
    // be prompted to allow it, we proceed.
    console.log(result.state)
    // if (result.state === 'granted' || result.state === 'prompt') {
    try {
      const data = await navigator.clipboard.readText();
      // console.log(data);
      if (filterByErrors) {
        setFilterByErrors(false);
      }
      setImportRawData(data, 'paste');
    } catch (err) {
      // alert('Paste Error: ' + JSON.stringify(err));
      // Snackbar instead of alert message, unsure how to reproduce this error. Maybe if clipboard permission is not allowed?
      //TODO: perhaps generate a nicer message to the user?
      appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
    }
    // } else {
    //   console.log(result.state)
    //   appNotify(`Unable to read from clipboard`, 'error');
    // }
  };

  // Clear click, hide validation error message and set import rows to []
  const handleClearClick = () => {
    resetDupCheckResult();
    setImportRows([]); // clear out rows
    setImportRowsSkippedCount(0);
  };

  const handleExport = () => {
    const timestamp = format(new Date(), '_yyyy-MM-dd_HHmmss');
    const fileName = 'ReVUe_PostAcquisitionTaskImport' + timestamp;
    const sheetName = 'Sheet';
    const customizeCell = options => {
      const { excelCell, gridCell } = options;
      const { data, column } = gridCell;
      if (!data?.invalidColumns?.length) return;
      if (data.invalidColumns.some(col => col.name === column.dataField)) { // Check if current cell's column is contained in invalidColumns
        excelCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '00ffc9c9' }
        }
      }
    }
    dataGridExtension.exportToExcel(gridRef, fileName, sheetName, customizeCell);
    sendClientLog('Info', 'Import - User exported post-acq task grid to Excel');
  }

  const onSubmitImport = () => {
    if (importRows.length === 0) {
      appNotify('Please Add Data', 'warning');
      return;
    }
    if (!canImport) {
      appNotify('Please fix invalid input', 'warning');
      return;
    }
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: handleSubmitImportConfirmation,
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: <>
        Are you sure you want to import {importRows.length} task{importRows.length > 1 ? 's' : ''}?{'\n\n'}
        <strong>Channel:</strong> {channel}{'\n'}
        <strong>Stage:</strong> {stage}{'\n'}
        <strong>Request Type:</strong> {requestType}
      </>
    });
  };

  const handleSubmitImportConfirmation = () => {
    closeConfirmationModal();
    submitImport(onSubmitImportSuccess);
    sendClientLog('Info', `Import - User clicked Import for ${importRows.length} post-acq task${importRows.length > 1 ? 's' : ''}.`);
  }
  const onSubmitImportSuccess = () => {
    resetDupCheckResult();
    // redirectToImportStatusTab(history);
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const resetDupCheckResult = () => {
    setImportDupCheckErrors([]);
  }

  const onFormSubmit = e => {
    e.preventDefault();
  }

  const onImportNameChange = e => {
    const value = e.target.value === '' ? null : e.target.value;
    setLocalImportName(value)
    _debounce(() => setImportName(value));
  }

  const handleChannelChange = e => {
    if (e.target.value !== channel) {
      // setPostAcqTaskImportChannel also resets requestType in the reducer
      setPostAcqTaskImportChannel(e.target.value);
    }
  }

  const handleRequestTypeChange = e => {
    if (e.target.value !== requestType) {
      setPostAcqTaskImportRequestType(e.target.value);
    }
  }

  const channelOptions = getChannels(taxonomy.entries);
  const requestTypeOptions = channel ? getRequestTypesForChannel(taxonomy.entries, channel) : [];

  const getErrorCells = () => {
    let errorArray = [];
    if (importRows.length) {
      importRows.forEach(row => {
        if (row.invalidColumns?.length > 0) {
          row.invalidColumns.forEach(column => {
            const err = errorArray.find(el => el.message === column.message);
            if (err) {
              err.count += 1;
            } else {
              errorArray.push({
                message: column.message,
                count: 1
              })
            }
          });
        }
      });
    }

    // Send all notifications in an array
    return errorArray;
  }

  const FileDropZoneWrapper = ({ children, disabled }) => {
    if (!disabled) return children;

    return <div style={{ position: 'relative' }}>
      <div style={{
        backgroundColor: 'rgb(214 214 214 / 72%)',
        position: 'absolute',
        width: '100%',
        height: '100%'
      }}/>
      {children}
    </div>
  }

  const onRemoveAllClick = errorType => {
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: () => handleRemoveAllErrorRows(errorType),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to remove all rows with errors?`
    });
  }

  const handleRemoveAllErrorRows = errorType => {
    closeConfirmationModal();
    let filteredRows;
    let deletedKeys = [];
    switch (errorType) {
      case 'format':
        filteredRows = importRows.filter(row => !row.invalidColumns?.length);
        revalidateImportRows(filteredRows);
        break;
      case 'dupCheck':
        filteredRows = importRows.filter(row => {
          const rowHasError = dupRowReferenceIds.includes(row.referenceId);
          if (rowHasError) {
            deletedKeys.push(row.referenceId);
          }
          return !rowHasError;
        });
        if (!deletedKeys.length) break;
        setImportRows(filteredRows);
        setImportDupCheckErrors(dupCheckErrors.filter(oldError => !deletedKeys.includes(oldError.referenceId)));
        break;
      default: return;
    }
  }

  const errorCells = getErrorCells();
  return (
    <>
      <Dialog open={channel === '' || requestType === ''}
        disablePortal
        style={{position: 'absolute'}}
        BackdropProps={{
          classes: { root: classes.backdrop }
        }}
        classes={{root: classes.root}}
        disableBackdropClick
      >
        <DialogTitle>Select a channel and request type to import</DialogTitle>
        <DialogContent>
          <FormControl style={{ minWidth: '160px', width: '100%', marginBottom: 12 }} className={classes.alignment}>
            <InputLabel htmlFor="channel-modal-helper">Channel</InputLabel>
            <Select
              value={channel}
              onChange={(e) => {
                handleChannelChange(e);
                gridRef.current?.instance.repaint();
              }}
              inputProps={{ id: 'channel-modal-helper' }}
            >
              {channelOptions.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ minWidth: '180px', width: '100%' }} className={classes.alignment} disabled={!channel}>
            <InputLabel htmlFor="requestType-modal-helper">Request Type</InputLabel>
            <Select
              value={requestType}
              onChange={(e) => {
                handleRequestTypeChange(e);
                gridRef.current?.instance.repaint();
              }}
              inputProps={{ id: 'requestType-modal-helper' }}
            >
              {requestTypeOptions.map(rt => (
                <MenuItem key={rt} value={rt}>{rt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      </Dialog>
      <div className={classes.container}>
        <div className={classes.leftControls}>
          <form autoComplete="off" onSubmit={onFormSubmit}>
            <FormControl style={{ minWidth: '120px', marginLeft: '0px', marginRight: '16px' }} className={classes.alignment}>
              <InputLabel htmlFor="channel-helper">Channel</InputLabel>
              <Select
                value={channel}
                onChange={handleChannelChange}
                inputProps={{ id: 'channel-helper' }}
              >
                {channelOptions.map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <div style={{ marginRight: '16px', display: 'inline-flex', flexDirection: 'column', verticalAlign: 'middle', minWidth: 132 }}>
              <Typography variant="caption" color="textSecondary">Stage</Typography>
              <div style={{ height: 32 }}>
                <Typography variant="body1" style={{ paddingTop: 2 }}>{stage || '—'}</Typography>
              </div>
              
            </div>
            <FormControl style={{ minWidth: '180px', marginRight: '16px' }} className={classes.alignment} disabled={!channel}>
              <InputLabel htmlFor="requestType-helper">Request Type</InputLabel>
              <Select
                value={requestType}
                onChange={handleRequestTypeChange}
                inputProps={{ id: 'requestType-helper' }}
              >
                {requestTypeOptions.map(rt => (
                  <MenuItem key={rt} value={rt}>{rt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </form>
          <Divider className={classes.verticalDivider} />
          <TextField
            error={importNameValidation.valid === false}
            label="List Name"
            onChange={onImportNameChange}
            value={localImportName ?? ''}
            helperText={importNameValidation.message}
            FormHelperTextProps={importNameValidation.isWarning ? { className: classes.isWarning } : {}}
          />
          <Divider className={classes.verticalDivider} />
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={onSubmitImport}
            disabled={!canImport || gridEditDirty || importNameValidation.valid === false || maxCountExceeded}
          >
            Import
          </Button>
        </div>
        <div className={classes.centerControls}>
          <Typography variant='h5' noWrap>Post-Acquisition Task Import</Typography>
        </div>
        <div className={classes.rightControls} >
          <Button variant='outlined' color='primary' onClick={handlePaste}>
            Paste from Clipboard
          </Button>
          <Button
            variant="outlined"
            color="primary"
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={handleClearClick}
            disabled={!importRows.length}
          >
            Clear
          </Button>
          <Tooltip title="Export with validation errors">
            <div>
              <Button
                variant="contained"
                color="primary"
                // style={{ marginLeft: '4px', marginRight: '4px' }}
                onClick={handleExport}
                // disabled={!importRows.length || canImport || filterByErrors}
                disabled={!importRows.length || gridEditDirty}
              >
                Export
              </Button>
            </div>
          </Tooltip>
          <Divider className={classes.verticalDivider} />
          <a href={xlsxSampleSheet} download="ReVUe_SampleImportPostAcq.xlsx" className={classes.xlsxSampleButton}>
            <Tooltip title="Download import template">
              <Button variant="outlined" color="primary">
                Sample Sheet
              </Button>
            </Tooltip>
          </a>
        </div>
      </div>
      <FileDropZoneWrapper>
        <FileDropZone
          width='100%'
          acceptedMimeTypes={[...excel.excelMimeTypes, 'text/plain']}
          onFilesAdded={onDropZoneFilesAdded}
          onFilesRejected={onDropZoneFilesRejected}
          elevation={2}
          dragOverElevation={10}
          className='gridFileDropZone'
          style={{ cursor: 'pointer', minHeight: '70px' }}
        />
      </FileDropZoneWrapper>
      {(errorCells.length || importRowsSkippedCount) && !maxCountExceeded
        ? <ImportExpander
          title='Cell errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('format')}
                    disabled={!errorCells.length || gridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {errorCells.map(error => {
            return (
              <div key={error.message} className={classes.errorTypeContainer}>
                <InlineBadge className={classes.errorCountBadge} badgeContent={error.count} error color='primary' />
                <Typography>{error.message}</Typography>
              </div>
            )
          })}
          {importRowsSkippedCount > 0
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={importRowsSkippedCount} color='primary' />
              <Typography>Row skipped - no Asset ID found</Typography>
            </div>
            : null
          }
        </ImportExpander>
        : null
      }
      {dupCheckErrors?.length
        ? <ImportExpander
          title='Import errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('dupCheck')}
                    disabled={gridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {dupCheckErrors.map(error => <div key={error.referenceId} className={classes.errorTypeContainer}><Typography>{error.errorMessage}</Typography></div>)}
        </ImportExpander>
        : null
      }
      <Paper style={{ flexGrow: 1, overflow: 'hidden' }} className={classes.gridPaper}>
        <ImportTasksGrid
          ref={gridRef}
        />
      </Paper>
    </>
  );
};

const styles = theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  leftControls: {
    display: 'flex',
    width: '40%',
    minWidth: 'fit-content',
    alignItems: 'center',
    minHeight: 68
  },
  rightControls: {
    display: 'flex',
    width: '40%',
    justifyContent: 'flex-end',
    marginBottom: 5
  },
  centerControls: {
    display: 'flex',
  },
  alignment: {
    margin: 0,
    verticalAlign: 'middle'
  },
  xlsxSampleButton: {
    textDecoration: 'none' // Remove text decoration underline from anchor tag to make it look like a button
  },
  verticalDivider: {
    width: 1,
    height: 36,
    margin: '0px 8px',
  },
  gridPaper: {
    padding: '5px 8px 8px'
  },
  errorTypeContainer: {
    display: 'flex',
    marginBottom: 1
  },
  errorCountBadge: {
    marginRight: 8
  },
  root: {
    position: 'absolute',
    zIndex: '1000 !important'
  },
  backdrop: {
    position: 'absolute'
  },
  isWarning: {
    color: theme.palette.warning.text + '!important'
  },
  redButton: {
    color: theme.palette.red.main,
    border: `1px solid ${theme.palette.red.main}80`,
    '&:hover': {
      border: `1px solid ${theme.palette.red.main}`,
      backgroundColor: `${theme.palette.red.main}0a`
    }
  },
  blueButton: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    }
  }
});

const mapStateToProps = state => {
  const importState = importTypeStateSelector(state, Constants.ImportType.POSTACQTASKS);
  const { channel, requestType, importRows } = importState;
  const stage = postAcqTaskImportStageSelector(state);
  const taxonomy = postAcquisitionTaxonomySelector(state);
  const canImport = channel && requestType && importRows?.length && !importRows.some(r => r.invalidColumns?.length > 0);
  return {
    channel,
    stage,
    requestType,
    taxonomy,
    canImport,  // this only means that import passed CLIENT validation
    importRows,
    importName: importState.importName,
    importNameValidation: importState.importNameValidation,
    gridEditDirty: importState.gridEditDirty,
    dupCheckErrors: importState.importDupCheckErrors,
    importRowsSkippedCount: importState.importRowsSkippedCount
  }
};
const mapDispatchToProps = dispatch => {
  const importType = Constants.ImportType.POSTACQTASKS;

  return {
    setPostAcqTaskImportChannel: channel => dispatch(setPostAcqTaskImportChannel(channel)),
    setPostAcqTaskImportRequestType: requestType => dispatch(setPostAcqTaskImportRequestType(requestType)),
    setImportRows: importRows => dispatch(setImportRows({ importType, importRows })),
    setImportRowsSkippedCount: importRowsSkippedCount => dispatch(setImportRowsSkippedCount({ importType, importRowsSkippedCount })),
    revalidateImportRows: (rows) => dispatch(revalidateImportRows({ importType, rows })),
    setImportDupCheckErrors: (dupCheckErrors) => dispatch(setImportDupCheckErrors({ importType, dupCheckErrors })),
    setConfirmationModalState: confirmationModalState => dispatch(setConfirmationModalState(confirmationModalState)),
    submitImport: onSubmitImportSuccess => dispatch(submitImport({ importType, onSubmitImportSuccess })),
    setImportName: importName => dispatch(setImportName({ importType, importName })),
    setImportNameValidation: importNameValidation => dispatch(setImportNameValidation({ importType, importNameValidation })),
    redirectToImportStatusTab: history => {
      history.push(Constants.Routes.UNDERWRITE_IMPORT_STATUS);
    },
    setImportRawData: (data, source) => dispatch(setImportRawData({ data, importType, source })),
    setImportLoadingOverlayState: (importLoadingOverlayState) => dispatch(setImportLoadingOverlayState({ importType, importLoadingOverlayState })),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options))
  }
};
export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ImportTasksControls);